
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import type { Project } from '../types';
import OptimizedImage from './OptimizedImage';
import { useProjectEditor } from '../pages/public/PublicLayout';

interface ProjectCardProps {
    project: Project;
}

const EditIcon: React.FC<{className?: string}> = ({className}) => (<svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>);

const ProjectCard: React.FC<ProjectCardProps> = ({ project }) => {
    const [isDevMode, setIsDevMode] = useState(false);
    const editorContext = useProjectEditor();

    useEffect(() => {
        setIsDevMode(sessionStorage.getItem('devMode') === 'true');
    }, []);

    const handleEditClick = (e: React.MouseEvent) => {
        e.preventDefault(); // Prevent navigating to project page
        e.stopPropagation();
        editorContext?.openProjectEditor(project);
    };

    const CardContent = () => (
        <>
            <OptimizedImage 
                src={project.images[0]?.url}
                size={800}
                fallbackSrc="https://picsum.photos/800/600"
                alt={project.title} 
                className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-500 ease-in-out"
            />
            <div className="absolute inset-0 bg-black bg-opacity-40 group-hover:bg-opacity-20 transition-opacity duration-300"></div>
            <div className="absolute bottom-0 left-0 p-6 text-white">
                <p className="text-sm uppercase tracking-widest font-header">{project.category}</p>
                <h3 className="text-2xl font-semibold mt-1 font-heading">{project.title}</h3>
            </div>
             {isDevMode && (
                <button
                    onClick={handleEditClick}
                    className="absolute top-4 right-4 z-20 p-2 bg-white/80 text-gray-800 rounded-full shadow-md opacity-0 group-hover:opacity-100 transition-opacity duration-300 hover:bg-white hover:scale-110"
                    title="Edit Project"
                >
                    <EditIcon className="w-5 h-5" />
                </button>
            )}
        </>
    );

    if (isDevMode) {
        // Use a div wrapper when in dev mode to allow the button to work correctly with drag-and-drop
        return (
            <div className="group relative overflow-hidden cursor-pointer aspect-[4/3] block">
                <Link to={`/project/${project.slug}`}>
                    <CardContent />
                </Link>
            </div>
        );
    }
    
    return (
        <Link 
            to={`/project/${project.slug}`}
            className="group relative overflow-hidden cursor-pointer aspect-[4/3] block"
        >
            <CardContent />
        </Link>
    );
};

export default ProjectCard;